import { useEffect } from 'react';
import type { ReactNode } from 'react';
import './primitives.css';

/**
 * Foundation primitive: Modal（Phase D §4.3 / Phase B 凍結対象 API）
 *
 * 背景オーバーレイ＋中央パネル。将来の JudgementModal（正誤判定演出）の土台。
 * パネル/オーバーレイの色は mood トークン（--mg-surface / --mg-border / --mg-overlay）経由。
 * - open=false の間は何も描画しない（DOM に残さない）。
 * - Esc キー・背景クリックで onClose を呼ぶ（onClose 未指定なら閉じ操作は無効）。
 */
export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  title?: string;
  children?: ReactNode;
}

export function Modal({ open, onClose, title, children }: ModalProps) {
  // Esc で閉じる。open かつ onClose がある時だけ購読し、確実に解除する。
  useEffect(() => {
    if (!open || !onClose) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="mg-modal-overlay"
      // 背景（オーバーレイ自身）をクリックした時だけ閉じる。パネル内は伝播で除外。
      onClick={onClose ? () => onClose() : undefined}
      role="presentation"
    >
      <div
        className="mg-modal-panel"
        role="dialog"
        aria-modal="true"
        // パネル内クリックがオーバーレイへ伝播して閉じてしまうのを防ぐ
        onClick={(e) => e.stopPropagation()}
      >
        {title ? <h2 className="mg-modal-title">{title}</h2> : null}
        {children}
      </div>
    </div>
  );
}
